// Check if CartCrosssell is already defined to prevent redeclaration
if (typeof window.CartCrosssell === 'undefined') {
  class CartCrosssell {
    constructor() {
      // Wait a short time to ensure elements are rendered before initialization
      setTimeout(() => {
        this.init();
        this.bindCartEvents();
      }, 300);
    }

    init() {
      this.container = document.querySelector('.cart-crosssell');
      if (!this.container) return;
      
      this.carousel = this.container.querySelector('.cart-crosssell__carousel');
      this.items = this.container.querySelectorAll('.cart-crosssell__item');
      this.prevButton = this.container.querySelector('.slider-button--prev');
      this.nextButton = this.container.querySelector('.slider-button--next');
      this.addButtons = this.container.querySelectorAll('.cart-crosssell__add-button');
      
      if (!this.items.length) return;
      
      this.position = 0;
      
      // Use fallback values if measurements are zero
      const firstItem = this.items[0];
      const itemStyle = window.getComputedStyle(firstItem);
      const itemWidth = firstItem.offsetWidth || 300; // Fallback to 300px if zero
      const itemMarginRight = parseInt(itemStyle.marginRight || '0') || 20; // Fallback to 20px if zero
      const actualItemWidth = itemWidth + itemMarginRight;
      
      // Calculate items visible - ensure slideWidth is never zero
      this.slideWidth = actualItemWidth > 0 ? actualItemWidth : 320;
      
      // Calculate how many items are visible at once (rounded down)
      const carouselWidth = this.carousel.offsetWidth || 400; // Fallback to 400px if zero
      this.visibleSlides = Math.floor(carouselWidth / this.slideWidth) || 1; // Ensure at least 1
      
      // Calculate maximum slide position based on number of items and visible slides
      this.maxPosition = Math.max(0, (this.items.length - this.visibleSlides) * this.slideWidth);
      
      // Reset position to ensure proper state
      this.carousel.style.transform = 'translateX(0)';
      
      this.bindEvents();
      this.updateNavigation();
    }

    bindCartEvents() {
      // Listen for cart updates
      document.addEventListener('cart:refresh', () => {
        this.init();
      });

      // Listen for cart drawer updates
      document.addEventListener('cart-drawer:refresh', () => {
        this.init();
      });
    }

    destroy() {
      if (!this.container) return;
      
      // Remove event listeners
      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.handlePrevClick);
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNextClick);
      }
      if (this.addButtons) {
        this.addButtons.forEach(button => {
          button.removeEventListener('click', this.handleAddToCart);
        });
      }
      window.removeEventListener('resize', this.handleResize);
    }

    // Reinitialize with a delay to ensure elements are properly rendered
    reinitialize() {
      setTimeout(() => {
        this.init();
      }, 300);
    }

    handlePrevClick = (e) => {
      e.preventDefault();
      this.slide('prev');
    }

    handleNextClick = (e) => {
      e.preventDefault();
      this.slide('next');
    }

    handleResize = () => {
      if (!this.items.length) return;
      
      // Get visible items (excluding ones with display:none)
      const visibleItems = Array.from(this.items).filter(item => item.style.display !== 'none');
      
      // Recalculate width including gap on resize
      const firstItem = this.items[0];
      if (firstItem) {
        const itemStyle = window.getComputedStyle(firstItem);
        const itemWidth = firstItem.offsetWidth || 300;
        const itemMarginRight = parseInt(itemStyle.marginRight || '0') || 20;
        this.slideWidth = itemWidth + itemMarginRight;
      }
      
      // Calculate how many items are visible at once
      this.visibleSlides = Math.floor(this.carousel.offsetWidth / this.slideWidth) || 1;
      
      // Calculate maximum slide position
      this.maxPosition = Math.max(0, (visibleItems.length - this.visibleSlides) * this.slideWidth);
      
      // Ensure position doesn't exceed maxPosition
      this.position = Math.min(this.position, this.maxPosition);
      
      this.carousel.style.transform = `translateX(-${this.position}px)`;
      this.updateNavigation();
    }

    handleAddToCart = (button) => {
      this.addToCart(button);
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.handlePrevClick);
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNextClick);
      }
      if (this.addButtons) {
        this.addButtons.forEach(button => {
          button.addEventListener('click', () => this.handleAddToCart(button));
        });
      }
      window.addEventListener('resize', this.handleResize);
    }

    slide(direction) {
      // Get visible items (excluding ones with display:none)
      const visibleItems = Array.from(this.items).filter(item => item.style.display !== 'none');
      
      // Recalculate maxPosition based on visible items
      this.maxPosition = Math.max(0, (visibleItems.length - this.visibleSlides) * this.slideWidth);
      
      if (direction === 'next') {
        this.position = Math.min(this.position + this.slideWidth, this.maxPosition);
      } else {
        this.position = Math.max(this.position - this.slideWidth, 0);
      }
      
      this.carousel.style.transform = `translateX(-${this.position}px)`;
      this.updateNavigation();
    }
    
    updateNavigation() {
      if (this.prevButton) this.prevButton.disabled = this.position <= 0;
      if (this.nextButton) this.nextButton.disabled = this.position >= this.maxPosition;
    }

    async addToCart(button) {
      const variantId = parseInt(button.dataset.variantId);
      const originalText = button.textContent;
      
      // Find the product card containing this button
      const productCard = button.closest('.cart-crosssell__item');
      
      try {
        button.disabled = true;
        button.innerHTML = '<div class="btn-spinner"></div>';
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        });

        if (!response.ok) throw new Error('Add to cart failed');

        // Parse the response
        const responseData = await response.json();
        
        // First update cart drawer content
        if (typeof theme.CartDrawer !== 'undefined' && theme.cart) {
          // Use theme's cart refresh functionality if available
          if (typeof theme.cart.refreshCart === 'function') {
            theme.cart.refreshCart();
          }
        }
        
        // Trigger cart refresh events
        document.dispatchEvent(new CustomEvent('cart:refresh', { 
          detail: { source: 'cart-crosssell', cartData: responseData }
        }));
        
        document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { 
          detail: { source: 'cart-crosssell', cartData: responseData }
        }));
        
        // Trigger cart build event
        document.dispatchEvent(new CustomEvent('cart:build'));
        
        // Fade out the product card
        if (productCard) {
          productCard.style.transition = 'opacity 0.5s ease-out';
          productCard.style.opacity = '0';
          
          // After transition completes, set display:none
          setTimeout(() => {
            productCard.style.display = 'none';
            
            // Re-position slides if needed
            this.handleResize();
          }, 500); // Match this timing with the transition duration
        }

        // Force a rebuild of the cart drawer after a short delay
        setTimeout(() => {
          // Re-fetch cart data to ensure latest state
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              // Manually trigger any cart updates
              if (typeof theme.CartDrawer !== 'undefined') {
                const cartDrawerForm = document.querySelector('#CartDrawerForm');
                if (cartDrawerForm && typeof theme.cartForm !== 'undefined') {
                  const cartForm = new theme.CartForm(cartDrawerForm);
                  cartForm.buildCart();
                }
              }
              
              // Re-initialize crosssell after cart update using the delayed method
              this.reinitialize();
            })
            .catch(err => console.error('Error refreshing cart:', err));
        }, 300);
        
      } catch (error) {
        console.error('Error adding to cart:', error);
        button.textContent = 'Error';
      } finally {
        button.disabled = false;
        setTimeout(() => {
          button.textContent = originalText || '+ Add';
        }, 1000);
      }
    }
  }

  // Make CartCrosssell globally available
  window.CartCrosssell = CartCrosssell;

  // Initialize the carousel when DOM is ready, but only if not already initialized
  if (!window.cartCrosssell) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        window.cartCrosssell = new CartCrosssell();
      });
    } else {
      window.cartCrosssell = new CartCrosssell();
    }
  }
} 